/**
 * Webメール連携 `/webmail`（A148 / A318）。
 *
 * `GET /ops/webmail/drift` は既にあり、ダッシュボードにも `webmail_drift` の
 * アラートが出ていたが、**是正する導線がどこにも無かった**。差分の中身
 * （expected / observed）も見えないので、運営には「何かがずれている」以上の
 * ことが分からなかった。
 *
 * `POST /ops/webmail/reprovision` は `organizationId` か `mailboxId` の
 * どちらか一方が必須（両方省略は 400）。`AURORA_SYNC_ENABLED=0` のときも
 * 400 を返すので、その旨を画面に出す。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, RefreshCw, TriangleAlert } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  PageHeader,
  StatusBadge,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { CodeBlock } from '../components/code-block.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { WebmailDriftEntry, WebmailInstanceEntry } from '../types/api.js';

const routeApi = getRouteApi(RID.webmail);

export function WebmailPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="Webメール連携"
        description="aurora-mail 側の状態と CP の食い違い（ドリフト）と、その是正。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'drift', label: 'ドリフト', icon: TriangleAlert },
          { value: 'instances', label: '連携先', icon: Inbox },
        ]}
      />

      {search.tab === 'drift' ? <DriftTab /> : null}
      {search.tab === 'instances' ? <InstancesTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------ ドリフト */

function DriftTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.webmailWrite);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const list = useCursorList<WebmailDriftEntry>({
    path: '/webmail/drift',
    keyFor: (params) => qk.webmailDrift(params),
    filters: {},
    refetchInterval: 60_000,
  });

  const columns: Array<DataTableColumn<WebmailDriftEntry>> = [
    { id: 'kind', header: '種別', cell: (d) => labelOf(d.kind) },
    {
      id: 'org',
      header: '契約組織',
      cell: (d) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: d.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {d.organizationId}
        </Link>
      ),
    },
    {
      id: 'mailbox',
      header: 'メールボックス',
      hideBelow: 'md',
      cell: (d) => (d.mailboxId ? <code className="font-mono text-xs">{d.mailboxId}</code> : '—'),
    },
    { id: 'detectedAt', header: '検知', cell: (d) => formatRelative(d.detectedAt) },
    {
      id: 'detail',
      header: <span className="sr-only">差分</span>,
      align: 'right',
      width: 'w-24',
      cell: (d) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((prev) => (prev === d.id ? null : d.id))}
        >
          {expanded === d.id ? '閉じる' : '差分'}
        </Button>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (d) =>
        canWrite ? (
          <ReprovisionButton
            organizationId={d.mailboxId ? undefined : d.organizationId}
            mailboxId={d.mailboxId ?? undefined}
          />
        ) : null,
    },
  ];

  const opened = list.rows.find((d) => d.id === expanded) ?? null;

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>ドリフトは「CP が正」で直します</AlertTitle>
        <AlertDescription>
          是正は CP の値を aurora-mail へ配り直す操作です（<code className="font-mono">webmail.reprovision</code>）。
          Webメール連携が無効（<code className="font-mono">AURORA_SYNC_ENABLED=0</code>）のときは実行できません。
        </AlertDescription>
      </Alert>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(d) => d.id}
        loading={list.query.isLoading}
        selectedKey={expanded}
        empty={<ListEmpty filtered={false} entityLabel="ドリフト" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      {opened ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              CP が期待する値
            </p>
            <CodeBlock value={JSON.stringify(opened.expected, null, 2)} language="json" />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              aurora-mail 側の実測
            </p>
            <CodeBlock value={JSON.stringify(opened.observed, null, 2)} language="json" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ReprovisionButton({
  organizationId,
  mailboxId,
}: {
  organizationId?: string;
  mailboxId?: string;
}): React.ReactElement {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () =>
      // どちらか一方だけを送る。両方省略すると 400。
      api.post('/webmail/reprovision', mailboxId ? { mailboxId } : { organizationId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ops', 'webmail'] }),
  });

  return (
    <Button variant="outline" size="sm" onClick={() => run.mutate()} loading={run.isPending}>
      <RefreshCw aria-hidden />
      再同期
    </Button>
  );
}

/* -------------------------------------------------------------- 連携先 */

function InstancesTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.webmailWrite);

  const list = useCursorList<WebmailInstanceEntry>({
    path: '/webmail/instances',
    keyFor: (params) => qk.webmailInstances(params),
    filters: {},
  });

  const columns: Array<DataTableColumn<WebmailInstanceEntry>> = [
    {
      id: 'org',
      header: '契約組織',
      cell: (w) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: w.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {w.organizationId}
        </Link>
      ),
    },
    {
      id: 'mailbox',
      header: 'メールボックス',
      hideBelow: 'md',
      cell: (w) => (w.mailboxId ? <code className="font-mono text-xs">{w.mailboxId}</code> : '—'),
    },
    {
      id: 'tenant',
      header: 'aurora テナント',
      hideBelow: 'lg',
      cell: (w) => <code className="font-mono text-xs">{w.auroraTenantId ?? '—'}</code>,
    },
    { id: 'state', header: '同期状態', cell: (w) => <StatusBadge status={w.syncState} size="sm" dot /> },
    {
      id: 'credentialVersion',
      header: '資格情報の版',
      align: 'right',
      hideBelow: 'lg',
      cell: (w) => w.credentialVersion,
    },
    { id: 'lastSyncedAt', header: '最終同期', cell: (w) => formatDateTime(w.lastSyncedAt) },
    {
      id: 'lastError',
      header: 'エラー',
      hideBelow: 'md',
      cell: (w) =>
        w.lastError ? (
          <Badge variant="destructive" size="sm" title={w.lastError}>
            あり
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (w) =>
        canWrite ? (
          <ReprovisionButton
            organizationId={w.mailboxId ? undefined : w.organizationId}
            mailboxId={w.mailboxId ?? undefined}
          />
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(w) => w.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="連携先" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </div>
  );
}
